import { useEffect, useRef, useState } from 'react';
import {
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  TextField,
  InputAdornment,
  Box,
  TablePagination,
  IconButton,
  Stack,
  Tooltip,
  Button,
  TableSortLabel,
} from '@mui/material';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import { useAppDispatch, useAppSelector } from '../../store';
import { findPayment } from '../../store/payment/api';
import Loader from '../../components/Loader';
import {
  reset as resetPayment,
  setStatus as setPaymentStatus,
} from '../../store/payment/paymentSlice';
import {
  setLimit,
  setSkip,
  setCurrentPage,
} from '../../store/payment/paymentSlice';
import AutoComplete from '../../components/AutoComplete';
import {
  findSelectAdvertisePackage,
  searchSelectAdvertisePackage,
} from '../../store/selectAdvertisePackage/api';
import {
  setSkip as setSkipAdvertisePackage,
  reset as resetAdvertisePackage,
} from '../../store/selectAdvertisePackage/selectAdvertisePackageSlice';
import { uniqBy } from 'lodash';
// import {
//   findSelectAdvertise,
//   searchSelectAdvertise,
// } from '../../store/selectAdvertise/api';
// import {
//   setSkip as setSkipAdvertise,
//   reset as resetAdvertise,
// } from '../../store/selectAdvertise/selectAdvertiseSlice';
import Label from '../../components/Label';
import { selectPaymentStatusColor } from '../../utils/selectPaymentStatusColor';
import { formatDate } from '../../utils/formatTime';
import { Link } from 'react-router-dom';
import { encryptString } from '../../utils/url';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { CSV_PAYMENT_HEADERS, PAYMENT_STATUS } from '../../constants';
import { csv } from './CSV';
import { findPaymentExport } from '../../store/paymentExport/api';
import { isValidArray } from '../../utils/valid';
import { CSVLink } from 'react-csv';
import { fileNameGeneraterWithoutType } from '../../utils/fileNameGenerater';
import { SortBy } from '../../enum';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.payment);
  const [search, setSearch] = useState<string>('');
  const [packageId, setPackageId] = useState<string>('');
  const [statusState, setStatusState] = useState<string>('');
  // const [advertiseId, setAdvertiseId] = useState<string>('');
  const exportCsvElement = useRef<any>(null);
  const [csvStructureData, setCsvStructureData] = useState<any>([]);
  const [sortBy, setSortBy] = useState(SortBy.advertiseNumber);
  const [sortByOrder, setSortByOrder] = useState('DESC');

  useEffect(() => {
    dispatch(
      findPayment({
        ...(search && { search: search }),
        limit,
        skip,
        ...(packageId && { packageId: packageId }),
        ...(statusState && { paymentStatus: statusState }),
        ...(sortBy && { sortByName: sortBy }),
        ...(sortByOrder && { sortByOrder: sortByOrder }),
      })
    );
  }, [
    dispatch,
    search,
    limit,
    skip,
    packageId,
    statusState,
    sortBy,
    sortByOrder,
  ]);

  useEffect(() => {
    dispatch(resetPayment());
  }, [search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setPaymentStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleCsvExport = async () => {
    try {
      await dispatch(
        findPaymentExport({
          ...(search && { search: search }),
          limit,
          skip,
          ...(packageId && { packageId: packageId }),
          ...(statusState && { paymentStatus: statusState }),
          ...(sortBy && { sortByName: sortBy }),
          ...(sortByOrder && { sortByOrder: sortByOrder }),
          // ...(advertiseId && { advertiseId: advertiseId }),
        })
      ).then((data: any) => {
        setCsvStructureData(csv(data?.payload?.data));
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (
      isValidArray(csvStructureData) &&
      exportCsvElement &&
      exportCsvElement.current &&
      exportCsvElement.current.link
    ) {
      exportCsvElement.current.link.click();
      setCsvStructureData([]);
    }
  }, [isValidArray(csvStructureData)]);

  const handleRequestSort = (property: any) => {
    const isAsc = sortBy === property && sortByOrder === 'ASC';
    setSortByOrder(isAsc ? 'DESC' : 'ASC');
    setSortBy(property);
  };

  // Advertise Package Data
  const {
    data: advertisePackageData,
    isLoading: isAdvertisePackageLoading,
    skip: advertisePackageSkip,
    hasMore: advertisePackageHasMore,
    status: advertisePackageStatus,
  } = useAppSelector((state) => state.selectAdvertisePackage);

  useEffect(() => {
    if (advertisePackageStatus === 'idle') {
      dispatch(
        findSelectAdvertisePackage({
          limit: 10,
          skip: advertisePackageSkip,
        })
      );
    }
  }, [advertisePackageSkip, advertisePackageStatus]);

  const onScrollFetchAdvertisePackage = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      advertisePackageHasMore
    ) {
      dispatch(setSkipAdvertisePackage({ skip: advertisePackageSkip + 10 }));
      dispatch(
        findSelectAdvertisePackage({
          limit: 10,
          skip: advertisePackageSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchAdvertisePackage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetAdvertisePackage());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipAdvertisePackage({ skip: 0 }));
    dispatch(searchSelectAdvertisePackage(params));
  };

  const uniqueAdvertisePackageData = uniqBy([...advertisePackageData], 'id');

  // Advertise Data
  // const {
  //   data: advertiseData,
  //   isLoading: isAdvertiseLoading,
  //   skip: advertiseSkip,
  //   hasMore: advertiseHasMore,
  //   status: advertiseStatus,
  // } = useAppSelector((state) => state.selectAdvertise);

  // useEffect(() => {
  //   if (advertiseStatus === 'idle') {
  //     dispatch(
  //       findSelectAdvertise({
  //         limit: 10,
  //         skip: advertiseSkip,
  //       })
  //     );
  //   }
  // }, [advertiseSkip, advertiseStatus]);

  // const onScrollFetchAdvertise = (
  //   e: React.UIEvent<HTMLUListElement, UIEvent>,
  //   search: string
  // ) => {
  //   if (
  //     e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
  //     advertiseHasMore
  //   ) {
  //     dispatch(setSkipAdvertise({ skip: advertiseSkip + 10 }));
  //     dispatch(
  //       findSelectAdvertise({
  //         limit: 10,
  //         skip: advertiseSkip + 10,
  //         ...(search && { search: search }),
  //       })
  //     );
  //   }
  // };

  // const searchAdvertise = (
  //   e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  // ) => {
  //   if (!e.target.value) {
  //     dispatch(resetAdvertise());
  //     return;
  //   }
  //   const params = {
  //     ...(!e.target.value && { limit: 10 }),
  //     ...(!e.target.value && { skip: 0 }),
  //     ...(e.target.value && { search: e.target.value }),
  //   };
  //   dispatch(setSkipAdvertise({ skip: 0 }));
  //   dispatch(searchSelectAdvertise(params));
  // };

  // const uniqueAdvertiseData = uniqBy([...advertiseData], 'id');

  return (
    <Page title={'Payment: List'}>
      <Container maxWidth={'xl'}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          spacing={{ xs: 0.5, sm: 1.5 }}
          my={1}
        >
          <CSVLink
            ref={exportCsvElement}
            headers={CSV_PAYMENT_HEADERS}
            data={csvStructureData}
            filename={fileNameGeneraterWithoutType('Payment', 'csv')}
            style={{ textDecoration: 'none' }}
            asyncOnClick={true}
          />
          <Tooltip title={'Export to download'}>
            <Button
              variant='contained'
              startIcon={<i className='fa-solid fa-file-csv'></i>}
              onClick={handleCsvExport}
            >
              {'Export CSV'}
            </Button>
          </Tooltip>
        </Stack>
        <Card sx={{ mt: 1.5 }}>
          <Grid
            container
            spacing={2}
            sx={{ py: 2.5, px: 3 }}
          >
            <Grid
              item
              xs={12}
              sm={4}
            >
              <TextField
                fullWidth
                onChange={(e) => setSearch(e.target.value)}
                placeholder={'Search...'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
            >
              <AutoComplete
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                  } else if (newValue) {
                    setPackageId(newValue.id);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (!newInputValue) {
                    setPackageId('');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.labelEn}</li>
                )}
                getOptionLabel={(option) => option.labelEn}
                fetchFunction={onScrollFetchAdvertisePackage}
                label={'Select Package'}
                loading={isAdvertisePackageLoading}
                options={uniqueAdvertisePackageData}
                searchFunction={searchAdvertisePackage}
                value={
                  uniqueAdvertisePackageData?.filter(
                    (advertisePackage) => advertisePackage.id === packageId
                  )?.[0] || null
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
            >
              <AutoComplete
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                  } else if (newValue) {
                    setStatusState(newValue.value);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (!newInputValue) {
                    setStatusState('');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.name}</li>
                )}
                getOptionLabel={(option) => option.name}
                fetchFunction={() => null}
                label={'Select Status'}
                loading={false}
                options={PAYMENT_STATUS}
                searchFunction={() => null}
                value={
                  PAYMENT_STATUS?.filter(
                    (status) => status.value === statusState
                  )?.[0] || null
                }
              />
              {/* <AutoComplete
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                  } else if (newValue) {
                    setAdvertiseId(newValue.id);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (!newInputValue) {
                    setAdvertiseId('');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.title}</li>
                )}
                getOptionLabel={(option) => option.title}
                fetchFunction={onScrollFetchAdvertise}
                label={'Select Advertise'}
                loading={isAdvertiseLoading}
                options={uniqueAdvertiseData}
                searchFunction={searchAdvertise}
                value={
                  uniqueAdvertiseData?.filter(
                    (advertise) => advertise.id === advertiseId
                  )?.[0] || null
                }
              /> */}
            </Grid>
          </Grid>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table
                size={'medium'}
                sx={{
                  borderCollapse: 'separate',
                  borderSpacing: '0 10px',
                }}
              >
                <TableHead
                  sx={{
                    background: '#f2d2df',
                  }}
                >
                  <TableRow>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.advertiseNumber}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() =>
                          handleRequestSort(SortBy.advertiseNumber)
                        }
                      >
                        <div style={{ color: '#64013c' }}>{'Ad No.'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.title}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.title)}
                      >
                        <div style={{ color: '#64013c' }}>{'Advertise'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.packageName}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.packageName)}
                      >
                        <div style={{ color: '#64013c' }}>{'Package'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.user}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.user)}
                      >
                        <div style={{ color: '#64013c' }}>{'User'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.paymentDate}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.paymentDate)}
                      >
                        <div style={{ color: '#64013c' }}>{'Date'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.paymentAmount}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.paymentAmount)}
                      >
                        <div style={{ color: '#64013c' }}>{'Amount'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.paymentStatus}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.paymentStatus)}
                      >
                        <div style={{ color: '#64013c' }}>{'Status'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell align='center'>
                          {payment?.advertiseNumber}
                        </TableCell>
                        <TableCell align='center'>{payment?.title}</TableCell>
                        <TableCell align='center'>
                          {payment?.payments?.[0]?.advertise_package?.labelEn ??
                            '-'}
                        </TableCell>
                        <TableCell align='center'>
                          {payment?.createdByUser?.name}
                        </TableCell>
                        <TableCell align='center'>
                          {payment?.payments?.[0]?.paymentDate
                            ? formatDate(payment?.payments?.[0]?.paymentDate)
                            : '-'}
                        </TableCell>
                        <TableCell align='center'>
                          {payment?.payments?.[0]?.paymentAmount
                            ? `QAR ${payment?.payments?.[0]?.paymentAmount}`
                            : '-'}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={selectPaymentStatusColor(
                              payment?.payments?.[0]?.paymentStatus ===
                                'pending'
                                ? 'Un-Paid'
                                : payment?.payments?.[0]?.paymentStatus
                            )}
                          >
                            {payment?.payments?.[0]?.paymentStatus === 'pending'
                              ? 'Un-Paid'
                              : payment?.payments?.[0]?.paymentStatus}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton aria-label='visibility'>
                            <Link
                              to={`/advertise/view/${encryptString(
                                payment?.id ?? ''
                              )}`}
                              style={{ color: '#808080' }}
                            >
                              <RemoveRedEyeIcon />
                            </Link>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Index;
